import VaultService from '~/services/vault.service'
import UploadService from '~/services/upload.service'
import memberhubConstants from '../../constants/memberhub.constants'
import { buildVaultUpdatePayload, resolveVaultBaselinePayload } from './update-payload'
import { cdrBillsSummary, cdrInvoiceList, cdrInvoiceToBill, hasCdrConsent } from '~/utils/energy-cdr/cdr-bills'
import { isEmailVerificationRequired } from '~/helpers/email-verification'
const vaultService = new VaultService()
const uploadService = new UploadService()

/*
 * Flattens an email-bills response (GET /forwarded-bills):
 * each item is one email holding `bills` (products) and `files` (attachments).
 * Every flattened bill/file back-references its parent email for status/context.
 * @param {Array} emails - array of email objects
 * @returns {{ bills: Array, files: Array }}
 */
const flattenBillEmails = emails => {
  const withEmail = email => item => ({
    ...item,
    email_id: email.id ?? email.uuid,
    email_status: email.status
  })
  return {
    bills: emails.flatMap(email => (email.bills || []).map(withEmail(email))),
    files: emails.flatMap(email => (email.files || []).map(withEmail(email)))
  }
}

/*
 * Read a vault straight from the API for diffing. Deliberately bypasses the
 * `show` action so an in-flight save never swaps out the product the funnel is
 * rendering, and stays silent so a failed lookup only costs the diff.
 * @param {string|number} productId
 * @returns {Promise<Object|null>}
 */
async function fetchVaultBaselinePayload (productId) {
  try {
    const r = await vaultService.show(productId)
    const response = await checkResponse(r, false)
    return response?.data?.payload ?? null
  } catch {
    return null
  }
}

const actions = {
  loader (status = true) {
    const layoutStore = useLayoutsStore()
    layoutStore.setPageLoader(status)
  },
  /*
   * Health vault form store action
   * @param context
   * @param payload
   * @returns {Promise<unknown>}
   */
  async saveVaultForm (payload) {
    const { $bus } = useNuxtApp()
    const route = useRoute()
    payload.formData.completion_source = route.query?.completion_source || null
    this.loader(true)
    const r = await vaultService.save(payload.formData)
    return checkResponse(r, true)
      .then(async response => {
        if(this.getSmartBillUploadFailedFilesForVaultForm?.length){
          await this.vaultFileSave({
            vaultId: response.data.id,
            files: this.getSmartBillUploadFailedFilesForVaultForm
          })
          this.setSmartBillUploadFailedFiles([])
        }
        $bus.emit('new-vault', response.data)
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Vault products list API service
   * @param context
   * @param payload
   * @returns {Promise<unknown>}
   */
  async vaultProducts () {
    this.vaultLoader = true
    const r = await vaultService.list()
    return checkResponse(r, false)
      .then(response => {
        this.setProduct({})
        this.resetInlineEditParams({})
        this.setLifeProduct({})
        this.setVaultsProductsList(response.data || [])
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.vaultLoader = false
        this.showEmptyProducts = !this.getProducts.length
      })
  },
  /*
 * Vault archived products list API service
 * @param context
 * @returns {Promise<unknown>}
 */
  async vaultArchivedProducts () {
    this.loader(true)
    const r = await vaultService.archiveList()
    return checkResponse(r, false)
      .then(response => {
        this.setVaultsArchivedProductsList(response.data || [])
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Forwarded bills list fetch store action
   * Fetches bills forwarded/imported via email and commits them to state.
   * @param showLoader
   * @returns {Promise<unknown>}
   */
  async fetchForwardedBills (showLoader = true) {
    this.loader(showLoader)
    const r = await vaultService.forwardedBills()
    return checkResponse(r, false)
      .then(response => {
        this.setForwardedEmails(response.data || [])
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Imported bills list fetch store action
   * Fetches the draft vaults found by scanning the member's connected inboxes
   * (GET /vaults/drafts) and commits them to state.
   * @param showLoader
   * @param {string} [accountId] - scopes the drafts to one connected account
   * @returns {Promise<unknown>}
   */
  async fetchImportedBills (showLoader = true, accountId = '') {
    this.loader(showLoader)
    const r = await vaultService.importedBills(accountId)
    return checkResponse(r, false)
      .then(response => {
        this.setImportedBills(response.data || [])
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Energy bill history fetch store action (CLUB-480 Phase 2)
   * Fetches a product's bill history once and caches it per vault id. Never
   * shows the page loader, and never rejects: on failure the product caches an
   * empty history, so Bills History stays hidden instead of breaking product
   * details.
   * A CDR vault reads GET /vaults/{vault}/cdr-bills instead, mapped to the same
   * raw bill shape, and is remembered so its bill details use the CDR endpoint.
   * @param vaultId - vault product uuid or id
   * @param {{cdr?: boolean}} [options] - cdr: the vault's bills come from CDR
   * @returns {Promise<void>}
   */
  async fetchBillHistory (vaultId, { cdr = false } = {}) {
    if (!vaultId || Object.prototype.hasOwnProperty.call(this.billHistory, vaultId)) {
      return
    }
    if (cdr) {
      this.cdrBillVaults = { ...this.cdrBillVaults, [vaultId]: true }
    }
    try {
      const r = await (cdr ? vaultService.cdrBills(vaultId) : vaultService.billHistory(vaultId))
      const response = await checkResponse(r, false)
      const bills = cdr
        ? cdrInvoiceList(response?.data).map(cdrInvoiceToBill).filter(Boolean)
        : response?.data || []
      if (cdr) {
        // cdr-bills sends the "N bills · past M months" figures itself
        this.billHistorySummary = { ...this.billHistorySummary, [vaultId]: cdrBillsSummary(response?.data) }
      }
      this.billHistory = { ...this.billHistory, [vaultId]: bills }
    } catch {
      this.billHistory = { ...this.billHistory, [vaultId]: [] }
    }
  },
  /*
   * Energy CDR consent fetch store action
   * Fetches whether a vault has a CDR consent once and caches it per vault id
   * (the raw consent, or null). Never rejects: a failure caches null, so the
   * vault is treated as not CDR-connected.
   * @param vaultId - vault product uuid or id
   * @returns {Promise<void>}
   */
  async fetchCdrConsent (vaultId) {
    if (!vaultId || Object.prototype.hasOwnProperty.call(this.cdrConsent, vaultId)) {
      return
    }
    try {
      const r = await vaultService.cdrConsent(vaultId)
      const response = await checkResponse(r, false)
      this.cdrConsent = { ...this.cdrConsent, [vaultId]: hasCdrConsent(response?.data) ? response.data : null }
    } catch {
      this.cdrConsent = { ...this.cdrConsent, [vaultId]: null }
    }
  },
  /*
   * Energy bill detail fetch store action (CLUB-480 Phase 2)
   * Fetches one bill's detail once and caches it per vault and bill id; the
   * history getter merges it into that bill. Never rejects: on failure nothing
   * is cached, so bill details shows the list fields and a later view retries.
   * @param vaultId - vault product uuid or id
   * @param billId - bill-history entry id
   * @returns {Promise<void>}
   */
  async fetchBillDetail (vaultId, billId) {
    if (!vaultId || billId == null || this.billDetails?.[vaultId]?.[billId]) {
      return
    }
    const cdr = !!this.cdrBillVaults?.[vaultId]
    // Failed per vault and bill: bill details shimmers while a detail loads, and
    // falls back to the list fields only once it has failed (cleared on retry).
    const setFailed = failed => {
      this.billDetailFailed = {
        ...this.billDetailFailed,
        [vaultId]: { ...(this.billDetailFailed?.[vaultId] || {}), [billId]: failed }
      }
    }
    setFailed(false)
    try {
      const r = await (cdr ? vaultService.cdrBill(vaultId, billId) : vaultService.billDetail(vaultId, billId))
      const response = await checkResponse(r, false)
      const detail = cdr ? cdrInvoiceToBill(response?.data) : response?.data
      if (!detail) {
        setFailed(true)
        return
      }
      this.billDetails = {
        ...this.billDetails,
        [vaultId]: { ...(this.billDetails?.[vaultId] || {}), [billId]: detail }
      }
    } catch {
      // Leave it uncached (a later view retries); bill details shows the list fields.
      setFailed(true)
    }
  },
  /*
   * Vault product details fetch store action
   * @param context
   * @param payload
   * @param showLoader
   * @returns {Promise<unknown>}
   */
  async show (payload, showLoader = true) {
    this.loader(showLoader)
    const r = await vaultService.show(payload)
    return checkResponse(r, false)
      .then(response => {
        this.setProduct(response.data)
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Vault LIC product details fetch store action
   * @param apiUrl
   * @param payload
   * @returns {Promise<void>}
   */
  async showLicDetails (apiUrl, payload) {
    this.loader(true)
    const r = await vaultService.licDetails(apiUrl, payload)
    return checkResponse(r, false)
      .then(response => {
        this.setLifeProduct(response.data)
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Fetch amply policy details with policy product id
   * @param payload
   * @param showLoader
   * @returns {Promise<unknown>}
   */
  async policy (payload, showLoader = true) {
    this.loader(showLoader)
    const r = await vaultService.policyDetails(payload)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Fetch amply to display combined policy details
   * @param payload
   * @param showLoader
   * @returns {Promise<unknown>}
   */
  async policyCombine (payload, showLoader = true) {
    this.loader(showLoader)
    const r = await vaultService.policyDetailsCombine(payload)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Vault product details update store action
   * Callers hand back the vault's whole stored payload, so the PATCH body is
   * diffed down to the fields the member actually edited.
   * @param context
   * @param payload
   * @returns {Promise<unknown>}
   */
  async update (payload) {
    const route = useRoute()
    const baseline = await resolveVaultBaselinePayload(this, payload.productId, fetchVaultBaselinePayload)
    const formData = {
      ...buildVaultUpdatePayload(payload.formData, baseline),
      completion_source: route.query?.completion_source || null
    }
    this.loader(true)
    const r = await vaultService.update(payload.productId, formData)
    return checkResponse(r, true, payload.schema)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Vault product rebate update store action
   * @param context
   * @param payload
   * @returns {Promise<unknown>}
   */
  async updateRebate (payload) {
    this.loader(true)
    const r = await vaultService.rebate(payload.productId, payload.formData)
    return checkResponse(r, true, payload.schema)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Delete vault product store action
   * @param context
   * @param payload
   * @returns {Promise<unknown>}
   */
  async deleteProduct (payload) {
    this.loader(true)
    const r = await vaultService.delete(payload)
    return checkResponse(r, true)
      .then(response => {
        this.removeProductFromList(payload)
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  async policies (payload) {
    const r = await vaultService.policies(payload)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  /*
   * Validate user for purchased product
   * @param payload
   * @returns {Promise<void>}
   */
  async checkPurchasedUser (payload) {
    this.loader(true)
    const r = await vaultService.validateUser(payload)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Save purchased user
   * @param payload
   * @returns {Promise<void>}
   */
  async savePurchasedUser (payload) {
    this.loader(true)
    const { $tracking } = useNuxtApp()
    const r = await vaultService.purchasedUserSignup(payload.formData)
    return checkResponse(r, true, payload.schema)
      .then(response => {
        $tracking.customEvent('My Account Sign up', { emailHashed: response.data?.hash_id })
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Fetching health policies for vault
   * @param payload
   * @returns {Promise<void>}
   */
  async fetchHealthPolicies (payload) {
    this.loader(true)
    const r = await vaultService.healthInsurancePolicies(payload)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  async fetchDuplicateHealthPolicies (payload) {
    this.loader(true)
    const r = await vaultService.duplicateHealthInsurancePolicies(payload)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Mutator for vault formData
   * @param state
   * @param payload
   */
  async setVaultFormData (payload) {
    await new Promise(resolve => {
      const formData = payload.formData || payload
      if (payload.model) {
        const arrayDiff = Object.keys(payload.model).filter(k => !Object.keys(payload.formData).includes(k))
        if (arrayDiff.length) {
          arrayDiff.forEach(item => {
            formData[item] = null
          })
        }
      }
      this.vaultFormData = { ...this.vaultFormData, ...formData }
      setTimeout(() => {
        resolve()
      })
    })
  },
  /*
   * Vault purchase API service
   * @param payload
   * @returns {Promise<T>}
   */
  async purchaseVaultSync () {
    this.isSyncing = true
    const r = await vaultService.purchaseSync()
    return checkResponse(r, false)
      .then(response => {
        if (response.data?.length) {
          this.setVaultsProductsList(response.data || [])
        }
        return Promise.resolve(response.data)
      })
      .catch(error => {
        // Unverified/guest member: vault sync reads Salesforce, which the API withholds
        // until the email is verified (CLUB-899). Nothing to sync - not an error.
        if (isEmailVerificationRequired(error)) {
          return Promise.resolve([])
        }
        return Promise.reject(error)
      })
      .finally(() => {
        this.isSyncing = false
      })
  },
  /*
   * Create lead from vault store action
   * @param payload
   * @returns {Promise<T>}
   */
  async createLeadFromVault (payload) {
    this.loader(true)
    const r = await vaultService.createLead(payload.vaultId, payload.params)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
  * Archive vault store action
  * @param vaultId
  * @returns {Promise<T>}
  */
  async vaultArchive (vaultId) {
    this.loader(true)
    const r = await vaultService.archive(vaultId)
    return checkResponse(r, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
  * Archive vault store action
  * @param vaultId
  * @returns {Promise<T>}
  */
  async vaultRestore (vaultId) {
    this.loader(true)
    const r = await vaultService.restore(vaultId)
    return checkResponse(r, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Vault file upload store action
   * @param formData
   * @returns {Promise<T>}
   */
  async vaultFileUpload (formData) {
    const r = await uploadService.new(formData)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  /*
   * Vault file save store action
   * @param payload
   * @returns {Promise<void>}
   */
  async vaultFileSave (payload) {
    this.loader(true)
    const r = await vaultService.saveFiles(payload.vaultId, { files: payload.files })
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Vault file rename store action
   * @param payload
   * @returns {Promise<void>}
   */
  async vaultFileRename (payload) {
    this.loader(true)
    const r = await vaultService.renameFile(payload.vaultId, payload.fileId, payload.formData)
    return checkResponse(r, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Vault file delete store action
   * @param payload
   * @returns {Promise<void>}
   */
  async vaultFileDelete (payload) {
    this.loader(true)
    const r = await vaultService.deleteFile(payload.vaultId, payload.fileId)
    return checkResponse(r, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * File delete store action
   * @param fileId
   * @returns {Promise<void>}
   */
  async fileDelete (fileId) {
    this.loader(true)
    const r = await uploadService.delete(fileId)
    return checkResponse(r, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  async fetchVendors (verticalId) {
    this.loader(true)
    const r = await vaultService.vendors({ vertical_id: verticalId })
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Vault rate rise confirm store action
   * @param payload
   * @returns {Promise<void>}
   */
  async confirmRateRise (payload) {
    this.loader(true)
    const r = await vaultService.rateRiseConfirm(payload.vaultId, payload.formData)
    return checkResponse(r, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Confirm health draft vault store action
   * @param vaultId
   * @returns {Promise<void>}
   */
  async confirmHealthDraft (vaultId) {
    this.loader(true)
    const r = await vaultService.confirmHealthDraft(vaultId)
    return checkResponse(r, true)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
 * Matches the address using the provided payload.
 *
 * @param {Object} payload - The payload containing address data to match.
 * @returns {Promise} - A promise that resolves with the response data or rejects with an error.
 */
  async addressMatch (payload) {
    this.loader(true)
    const r = await vaultService.addressMatch(payload)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },

  /*
 * Updates the lead ID for a specific vault.
 *
 * @param {Object} params - The parameters containing vaultId and uuid.
 * @param {string} params.vaultId - The ID of the vault to update.
 * @param {string} params.uuid - The UUID to update the lead ID with.
 * @returns {Promise} - A promise that resolves with the response data or rejects with an error.
 */
  async updateLeadId ({ vaultId, uuid }) {
    this.loader(true)
    const r = await vaultService.updateLeadId(vaultId, uuid)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
 * Fetches the list of verticals from the vault service.
 *
 * @returns {Promise<unknown>} - A promise that resolves with the response data or rejects with an error.
 */
  async fetchVerticals () {
    // No loader(true) here on purpose, and therefore no loader(false): the
    // layouts store keeps pageLoader as a STACK (setPageLoader pushes on
    // truthy, splices one entry on falsy), so an unbalanced loader(false)
    // popped an entry another in-flight call had pushed and cleared its
    // loader early.
    const r = await vaultService.verticals()
    return checkResponse(r, false)
      .then(response => {
        this.setVerticals(response.data)
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  /*
   * Fetches the details of a purchased product using its UUID.
   *
   * @param {string} uuid - The unique identifier of the purchased product.
   * @returns {Promise<*>} - A promise that resolves with the product details or rejects with an error.
   */
  async fetchPurchasedProductDetails (uuid) {
    const r = await vaultService.purchasedProductDetails(uuid)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  async parseUploadedBill (uuid) {
    const route = useRoute()
    const formData = {}
    formData.completion_source = route.query?.completion_source || null
    formData.storage_consent = this.smartBillUpload.storageConsent || false
    const r = await uploadService.billParse(uuid, formData)
    return checkResponse(r, false,[])
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  async fetchParsedDetails (uuid) {
    const r = await uploadService.billDetails(uuid)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  },
  setVerticals (payload) {
    setLocal('verticals', JSON.stringify(payload))
    this.verticals = payload
  },
  /*
   * Mutator for vault formData
   * @param state
   */
  resetVaultFormData () {
    this.vaultFormData = {}
  },
  /*
   * Mutator for vault products list
   * @param state
   * @param payload
   */
  setVaultsProductsList (payload) {
    this.products = payload || []
  },
  /*
 * Mutator for vault products list
 * @param state
 * @param payload
 */
  setVaultsArchivedProductsList (payload) {
    this.archivedProducts = payload || []
  },
  /*
   * Mutator for forwarded emails + bills.
   * Each response item is one inbound email holding a `bills` array of products;
   * we keep the raw emails (forwardedEmails) and, separately, a flattened list of
   * every product across all emails (forwardedBills) with a back-reference to its
   * parent email for status/context.
   * @param payload - array of email objects
   */
  setForwardedEmails (payload) {
    const emails = payload || []
    // All attachments across every email are kept too (failed ones surface as
    // failed tiles).
    const { bills, files } = flattenBillEmails(emails)
    this.forwardedEmails = emails
    this.forwardedBills = bills
    this.forwardedFiles = files
    this.forwardedBillsLoaded = true
  },
  /*
   * Mutator for imported bills: the draft vaults as the API returns them
   * (already one item per bill, so nothing to flatten).
   * @param payload - array of draft vaults
   */
  setImportedBills (payload) {
    this.importedBills = payload || []
    this.importedBillsLoaded = true
  },
  /*
   * Mutator for vault products list reset
   * @param state
   */
  resetProductList () {
    this.products = []
  },
  /*
   * Resets the inline edit parameters to their initial state.
   * This method is used to clear any temporary data or states associated with inline editing features,
   * effectively resetting any changes made during the editing process.
   */
  resetInlineEditParams () {
    this.inlineEditParams = {}
  },
  /*
   * Resets the inline edit state.
   * This method is intended to reset the `isInlineEdit` flag to `false`, effectively
   * turning off inline editing mode. However, it incorrectly calls itself recursively,
   * leading to a stack overflow error. This method needs to be corrected to avoid
   * calling itself.
   */
  resetInlineEdit () {
    this.resetInlineEditParams()
    this.isInlineEdit = false
  },
  /*
   * Mutator for vault products list update
   * @param state
   * @param productId
   */
  removeProductFromList (productId) {
    const productIndex = this.products.indexOf(this.products.find(p => p.uuid === productId))
    if (productIndex >= 0) {
      this.products.splice(productIndex, 1)
    }
  },
  /*
   * Mutator for vault product details
   * @param state
   * @param payload
   */
  setProduct (payload) {
    this.product = payload
    this.product_fetched_at = payload?.uuid ? Date.now() : null
    if (payload?.payload) {
      if (payload?.payload?.policy_details && payload?.payload?.policy_details.product_type === 'Hospital' && payload?.payload?.cover_type === 'Combined') {
        this.product.payload.extra_cover_exists = true
      }

      const currentFormData = JSON.parse(JSON.stringify(payload?.payload))
      if(parseInt(currentFormData.vertical) === memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE) {
        if (currentFormData && currentFormData.policy_details) {
          delete currentFormData.policy_details.hospital_covers
          delete currentFormData.policy_details.comparison_hospital_covers
          delete currentFormData.policy_details.comparison_extra_covers
          delete currentFormData.policy_details.extra_covers
          delete currentFormData.policy_details.extra_details
          delete currentFormData.policy_details.policy_features
        }
        if (currentFormData && currentFormData.extra_details) {
          delete currentFormData.extra_details.hospital_covers
          delete currentFormData.extra_details.comparison_hospital_covers
          delete currentFormData.extra_details.comparison_extra_covers
          delete currentFormData.extra_details.extra_covers
          delete currentFormData.extra_details.extra_details
          delete currentFormData.extra_details.policy_features
        }
        if (currentFormData?.income_tier_rebate) {
          delete currentFormData?.income_tier_rebate
        }
        if (currentFormData?.extra_cover_exists) {
          delete currentFormData?.extra_cover_exists
        }
        if(!currentFormData?.excess) {
          currentFormData.excess = currentFormData.policy_details?.excess || ''
        }
        if(!currentFormData.cover_type){
          if(!currentFormData.policy_details?.hospital_tier){
            currentFormData.cover_type = 'GeneralHealth'
          }else if(currentFormData.policy_details?.hospital_tier && currentFormData.policy_details?.extra_cover_level){
            currentFormData.cover_type = 'Combined'
          }else{
            currentFormData.cover_type = 'Hospital'
          }
        }
        if(!currentFormData?.hospital_cover_level) {
          currentFormData.hospital_cover_level = currentFormData.policy_details?.hospital_tier || ''
        }
      }
      this.vaultFormData = currentFormData
    } else {
      this.vaultFormData = {}
    }
  },
  /*
   * Set life product details
   * @param payload
   */
  setLifeProduct (payload) {
    this.lifeProduct = payload
  },
  async initVaultsProductsList () {
    if(!this.products.length) {
      await this.vaultProducts()
      return Promise.resolve()
    }
    return Promise.resolve()
  },
  /*
   * Forwarded bills init guard.
   * De-duplicates the fetch so the shared list is only pulled once across the
   * dashboard / forwarded-bills pages a member can land on from the email CTA.
   * @returns {Promise<void>}
   */
  async initForwardedBills (showLoader = false) {
    // Guard on a "loaded" flag (not list length) so an empty result still counts
    // as initialised and doesn't re-fetch on every mount/navigation.
    if (!this.forwardedBillsLoaded) {
      await this.fetchForwardedBills(showLoader)
      return Promise.resolve()
    }
    return Promise.resolve()
  },
  /*
   * Fetch confidence score for specific vault
   * @param vault_id
   * @returns {Promise<*|{}>}
   */
  async getConfidenceScore (vault_id) {
    const service = new VaultService()
    const response = await service.getConfidenceScore(vault_id)
    const scoreData = response.data || {}
    
    // Update the current product with- latest score so UI reactively displays
    if (this.product && this.product.id === vault_id) {
      this.product.latest_score = scoreData
    }
    
    return scoreData
  },
  setSmartBillUploadFailedFiles (payload = []){
    this.smartBillUpload.failedFiles = payload || []
  },
  setSmartBillUploadedFiles (payload = []){
    this.smartBillUpload.uploadedFiles = payload || []
  },
  setSmartBillStorageConsent (payload = false) {
    this.smartBillUpload.storageConsent = payload
  },
  /*
   * Unlock review status for a purchased health insurance vault product.
   * Calls POST /vaults/{vaultId}/review-status/optin, then patches the matching
   * product in the store's products list in-place using the returned fields so the
   * card re-renders immediately without waiting for the follow-up vaultProducts() re-fetch.
   * @param {string} vaultId
   * @returns {Promise}
   */
  async vaultUnlockReviewStatus (vaultId) {
    this.loader(true)
    const r = await vaultService.unlockReviewStatus(vaultId)
    return checkResponse(r, false)
      .then(response => {
        const data = response.data
        if (data) {
          const product = this.products.find(p => p.uuid === vaultId)
          if (product) {
            product.review_status_locked = data.review_status_locked
            product.show_review_status = data.show_review_status
            product.review_status_unlock_eligible = data.review_status_unlock_eligible
          }
        }
        return Promise.resolve(data)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        this.loader(false)
      })
  }
}
export default actions
